import React, { useState } from "react"
import { motion } from "framer-motion"
import {ChevronDown, Search} from 'lucide-react'

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState("")

  const faqCategories = [
    {
      category: "Equipment Selection",
      questions: [
        {
          question: "How do I choose the right mule to move shed for my operation?",
          answer: "Equipment selection depends on several factors: maximum shed weight you'll handle, terrain conditions (flat vs. hilly), frequency of use, and distance of moves. For sheds under 20,000 lbs on flat terrain, a standard model may suffice. For larger structures or challenging terrain, consider higher capacity models like the Mule 628T with 28,000 lbs capacity and enhanced stability features."
        },
        {
          question: "What's the difference between basic and professional-grade shed moving equipment?",
          answer: "Professional-grade equipment typically features reinforced frames, hydraulic lift systems (vs. manual), remote control operation, dual-axle stability, and higher weight capacities. Basic models are suitable for occasional residential moves, while professional units are built for daily commercial use with enhanced durability and safety features."
        },
        {
          question: "Should I buy new or used shed moving equipment?",
          answer: "This depends on your budget and usage frequency. New equipment offers full warranty coverage, latest safety features, and no wear concerns - ideal for businesses planning high-volume operations. Quality used equipment can provide 50-70% cost savings and is suitable for part-time operators or those starting out. Always inspect hydraulic systems, structural integrity, and service records when considering used units."
        }
      ]
    },
    {
      category: "Capacity & Performance",
      questions: [
        {
          question: "What does the weight capacity rating actually mean?",
          answer: "The capacity rating indicates the maximum safe load the equipment can lift and transport under ideal conditions (level ground, proper balance, standard configuration). Always include a 20% safety margin - for example, if moving a 20,000 lb shed, use equipment rated for at least 24,000 lbs. Capacity decreases on slopes or uneven terrain."
        },
        {
          question: "How does terrain affect shed moving capabilities?",
          answer: "Terrain significantly impacts performance. Level ground allows full-rated capacity. Each 5-degree slope reduces effective capacity by approximately 15%. Soft soil, gravel, or muddy conditions require tire flotation considerations and may need reinforced ground protection. Hills exceeding 10 degrees typically require specialized equipment or alternative moving methods."
        },
        {
          question: "What's the typical ground clearance needed for shed moving mule operations?",
          answer: "Standard shed moving equipment requires 12-16 inches of clearance between the shed floor and ground for positioning. The mule itself typically has 6-8 inches ground clearance for navigation. Ensure your work area is free of obstructions, utilities, and debris within this height range."
        }
      ]
    },
    {
      category: "Operation & Safety",
      questions: [
        {
          question: "What operator training is recommended for shed moving equipment?",
          answer: "While no federal license is required, comprehensive training should cover: equipment familiarization, hydraulic system operation, load balancing principles, terrain assessment, emergency procedures, and proper maintenance protocols. Many recommend 40+ hours supervised operation before solo work, especially for commercial operations."
        },
        {
          question: "What are the most common safety hazards in shed moving operations?",
          answer: "Primary hazards include: tipping due to improper load balance, hydraulic system failures, overhead power line contact, ground collapse under weight, pinch points during positioning, and visibility limitations. Always conduct pre-operation site surveys, maintain proper clearances, use spotters, and follow manufacturer safety guidelines."
        },
        {
          question: "How do weather conditions affect shed moving operations?",
          answer: "Weather is critical for safety. Avoid operations during: winds exceeding 20 mph (risk of instability), rain/wet conditions (reduced traction and visibility), temperatures below 20°F (hydraulic fluid viscosity issues), or lightning within 10 miles. Ice and snow create extremely hazardous conditions and should halt operations entirely."
        }
      ]
    },
    {
      category: "Maintenance & Care",
      questions: [
        {
          question: "What maintenance does shed moving equipment require?",
          answer: "Daily: Visual inspection, hydraulic fluid level check, tire pressure verification. Weekly: Lubricate moving parts, inspect hydraulic hoses for wear, check structural bolts. Monthly: Change hydraulic filters, inspect electrical systems, test safety mechanisms. Annually: Comprehensive hydraulic system service, structural weld inspection, load test verification."
        },
        {
          question: "How often should hydraulic fluid be changed?",
          answer: "Hydraulic fluid should be changed every 500 operating hours or annually, whichever comes first. However, if fluid shows signs of contamination (dark color, burnt smell, particles), change immediately. Use only manufacturer-recommended hydraulic fluid grades - typically ISO 46 or 68 for most shed moving equipment."
        },
        {
          question: "What are signs that equipment needs immediate maintenance?",
          answer: "Critical warning signs: unusual noises (grinding, squealing), hydraulic fluid leaks, sluggish lift response, visible structural cracks, excessive vibration, uneven lifting, tire wear patterns indicating misalignment, or hydraulic overheating. Stop operations immediately if any of these occur and consult qualified technicians."
        }
      ]
    },
    {
      category: "Cost & Logistics",
      questions: [
        {
          question: "What factors influence shed mule cost?",
          answer: "Primary cost factors: weight capacity (higher capacity = higher cost), features (remote control, hydraulic vs. manual), condition (new vs. used), brand reputation, included accessories, warranty coverage, and regional market variations. Typical range varies from entry-level models to premium commercial-grade units with specialized features."
        },
        {
          question: "Are there hidden costs beyond the equipment purchase price?",
          answer: "Yes, budget for: annual maintenance (10-15% of purchase price), insurance coverage, transportation/delivery fees, operator training, spare parts inventory, seasonal storage if applicable, and potential permitting requirements for commercial operations. Also consider financing costs if not purchasing outright."
        },
        {
          question: "How does mule shed delivery work for equipment purchases?",
          answer: "Delivery logistics vary by seller and location. Typical considerations: freight shipping for long distances (requires forklift/loading dock at destination), flatbed truck delivery for regional transport, or self-pickup options. Delivery can add 5-15% to total cost depending on distance. Verify delivery method, timeline, and any unloading assistance included before purchase."
        }
      ]
    },
    {
      category: "Technical Specifications",
      questions: [
        {
          question: "What's the difference between hydraulic and mechanical lift systems?",
          answer: "Hydraulic systems use pressurized fluid to provide smooth, controlled lifting with precise positioning and reduced operator effort. They handle higher capacities and offer better load stability. Mechanical systems (winch/cable) are simpler, require more physical effort, have slower operation, but are more economical and easier to maintain for lighter-duty applications."
        },
        {
          question: "What power source options exist for shed moving equipment?",
          answer: "Most modern equipment uses gasoline or diesel engines (5-15 HP range) for hydraulic pump power. Electric/battery models exist for indoor applications but have limited runtime. Diesel offers better fuel efficiency and torque for heavy-duty operations. Some units feature dual power systems - engine for hydraulics, battery for remote control functions."
        },
        {
          question: "How important is tire selection for shed moving operations?",
          answer: "Tire selection is critical for stability, traction, and ground protection. Pneumatic tires provide better shock absorption and traction on uneven terrain. Solid rubber tires eliminate puncture risk and work well on smooth surfaces. Wider tires (12+ inches) distribute weight better on soft ground. Always maintain manufacturer-recommended pressure for load capacity."
        }
      ]
    }
  ]

  const filteredFAQs = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(q =>
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0)

  return (
    <div className="bg-zinc-900 min-h-screen">
      {/* Header */}
      <section className="bg-zinc-950 py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Frequently Asked <span className="text-orange-500">Questions</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Educational resource covering common questions about shed moving equipment, operations, maintenance, and best practices
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-800 text-white pl-12 pr-4 py-4 rounded-lg border border-zinc-700 focus:border-orange-500 focus:outline-none"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredFAQs.length > 0 ? (
            <div className="space-y-12">
              {filteredFAQs.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <h2 className="text-3xl font-bold text-white mb-6 pb-3 border-b border-orange-500/30">
                    <span className="text-orange-500">{category.category}</span>
                  </h2>
                  <div className="space-y-4">
                    {category.questions.map((faq, faqIndex) => {
                      const globalIndex = categoryIndex * 100 + faqIndex
                      const isOpen = openIndex === globalIndex

                      return (
                        <motion.div
                          key={faqIndex}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: faqIndex * 0.1 }}
                          viewport={{ once: true }}
                          className="bg-zinc-800 rounded-lg border border-zinc-700 hover:border-orange-500/50 transition-all"
                        >
                          <button
                            onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                            className="w-full px-6 py-5 flex items-center justify-between text-left"
                          >
                            <span className="text-lg font-bold text-white pr-4">
                              {faq.question}
                            </span>
                            <ChevronDown
                              className={`text-orange-500 flex-shrink-0 transition-transform duration-300 ${
                                isOpen ? "transform rotate-180" : ""
                              }`}
                              size={24}
                            />
                          </button>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              transition={{ duration: 0.3 }}
                              className="px-6 pb-5"
                            >
                              <p className="text-gray-300 leading-relaxed">
                                {faq.answer}
                              </p>
                            </motion.div>
                          )}
                        </motion.div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">
                No FAQs match your search. Try different keywords.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Additional Resources CTA */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Explore our comprehensive guides, blog articles, and technical resources for more detailed information
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/buying-guide"
              className="inline-block bg-white hover:bg-gray-100 text-orange-600 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl"
            >
              Buying Guide
            </a>
            <a
              href="/safety"
              className="inline-block bg-zinc-900 hover:bg-zinc-800 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl border border-white/20"
            >
              Safety Guidelines
            </a>
            <a
              href="/maintenance"
              className="inline-block bg-zinc-900 hover:bg-zinc-800 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl border border-white/20"
            >
              Maintenance Tips
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default FAQPage